import { useContext, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { AdminPageHeader, Form, AdminFormAction } from '@ui';
import { useSetHomeLocation } from '@lp/core/hooks/useSetHomeLocation';
import {
  ContractorForm,
  useContractorFormSchema,
  getDefaultContractorValues,
} from '@lp/features/admin/forms/ContractorForm';
import { AuthContext } from '@lp/core/auth';
import { ContractorFormType, EmployeeFormValues } from '@/types';
import { Employee } from '@/api/apis/resources';
import {
  useActivateEmployeeMutation,
  useDeactivateEmployeeMutation,
  useUpdateEmployeesMutation,
} from '@/queries/employee/employee.mutation';
import { FileType } from '@/queries/file';
import { useLoaderData } from '@/hooks/useLoaderData';
import { useUploadFile } from '@/hooks/useUploadFile';
import { getChangedValues, remapKeys } from '@/utils/helpers';
import { Location } from '@/api/types';
import { retrieveUnifiedError } from '@/api/utils';
import { AirportType, Hub, TXCenter } from '@/api/apis/facilities';
import { OpoOrganization } from '@/api/apis/opo';
import { AdminPermissions } from '@/permissions';

type ContractorsLoader = {
  employee: Employee;
  homeLocation?: Location;
  airport?: AirportType;
  baseLocation?: Hub;
  txCenter?: TXCenter;
  opo?: OpoOrganization;
};

export const ContractorsEdit = () => {
  const navigate = useNavigate();
  const { employee, homeLocation, airport, baseLocation, txCenter, opo } = useLoaderData<ContractorsLoader>();
  const { mutateAsync } = useUpdateEmployeesMutation();
  const setHomeLocation = useSetHomeLocation(homeLocation?.id);
  const { mutateAsync: deactivateAsync } = useDeactivateEmployeeMutation();
  const { mutateAsync: activateAsync } = useActivateEmployeeMutation();
  const { uploadFiles } = useUploadFile(FileType.Resource);
  const { checkPermission } = useContext(AuthContext);

  const hasEmployeeEditPermission = checkPermission(AdminPermissions.TmdxUsersUpdate);
  const { schema, isLoading: isGeoDataLoading } = useContractorFormSchema();

  const defaultValues = useMemo<ContractorFormType>(
    () => getDefaultContractorValues(employee, homeLocation, baseLocation),
    [employee, homeLocation, baseLocation],
  );

  const handleBackToList = () => {
    navigate(`/admin/users/contractors`);
  };

  const handleSubmit = async (values: ContractorFormType) => {
    const {
      country,
      addressLine,
      state,
      city,
      postalCode,
      latitude,
      longitude,
      homeLocationTimezone,
      affiliationType,
    } = values;
    const locationValues = { country, addressLine, state, city, postalCode, latitude, longitude };

    try {
      let uploadedFile;

      if (values.image) {
        uploadedFile = await uploadFiles([values.image]);
      }

      const updateBody = {
        profilePhoto: uploadedFile ? uploadedFile[0].fileName : values.profilePhoto,
        phone: values.phone,
        tsaPrecheckNumber: values.tsaPrecheckNumber,
        isActive: values.isActive,
        roles: values.roles,
        organSpecializations: values.organType,
        contractorCertifications: values.contractorCertifications,
        affiliationId: values.affiliationTxCenterId || values.affiliationOpoId,
        hireDate: values.hireDate,
        scrubSizeTop: values.scrubSizeTop,
        scrubSizeBottom: values.scrubSizeBottom,
        weight: values.weight,
        baseLocationId: values.baseLocationId,
        closestAirportForDeployment: values.closestAirportForDeploymentId
          ? Number(values.closestAirportForDeploymentId)
          : null,
        locationPreference: values.locationPreference,
      };
      const updatedLocation = {
        ...locationValues,
        timezone: homeLocationTimezone || '',
      };

      let homeLocationId = homeLocation?.id;
      let isLocationChanged = false;
      if (homeLocation) {
        const locationChangedValues = getChangedValues(updatedLocation, homeLocation);
        isLocationChanged = Object.keys(locationChangedValues).length > 0;
      }

      if (isLocationChanged || !homeLocation) {
        homeLocationId = await setHomeLocation(updatedLocation);
      }
      const changedValues = getChangedValues<Partial<EmployeeFormValues>>(updateBody, defaultValues);
      await mutateAsync({
        id: employee.id,
        ...remapKeys(changedValues, { baseLocationId: 'baseLocation' }),
        homeLocation: homeLocationId,
        affiliationType,
      });

      if ('isActive' in changedValues) {
        const mutationFn = changedValues.isActive ? activateAsync : deactivateAsync;
        await mutationFn(employee.id);
      }
      toast.success('Contractor updated successfully.');
      handleBackToList();
    } catch (e) {
      const error = retrieveUnifiedError(e, "Can't update contractor");
      toast.error(error.message);
    }
  };

  if (isGeoDataLoading) return null;

  return (
    <Form<ContractorFormType> defaultValues={defaultValues} schema={schema} onSubmit={handleSubmit}>
      <div className="flex flex-col gap-9">
        <AdminPageHeader title="Edit Contractor" backToListLink="/admin/users/contractors">
          <AdminFormAction type="save" handleClick={handleBackToList} hasEditPermissions={hasEmployeeEditPermission} />
        </AdminPageHeader>
        <ContractorForm
          initialAirportName={airport?.name}
          txCenter={txCenter}
          opo={opo}
          isReadOnly={!hasEmployeeEditPermission}
          baseLocation={baseLocation}
        />
      </div>
    </Form>
  );
};
